import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import moment from "moment";
import { Text, CFontIcon, CCheckbox } from "@appcues/component-library";

import QueryResults from "components/Common/QueryResults";
import Pagination from "components/Common/UI/Pagination/Pagination";
import CoolTip from "components/Common/CoolTip";
import {
  TableContainer,
  TableData,
  TableRow,
  TableWrapper
} from "components/Common/UI/Table/Styled";

import TableHeader from "./Common/TableHeader";
import TableOverlay from "./Common/TableOverlay";

const ResultsWrapper = styled.div`
  position: relative;
  height: 500px;
`;

const CheckboxWrapper = styled.div`
  margin: 0 -5px 0 0;
  label {
    margin: 0 !important;
  }
  .checkbox {
    margin: 0 !important;
  }
`;

const Footer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 1em;
`;

export const Application = ({
  results,
  currentPage,
  totalResults,
  pageSize,
  orderByColumn,
  orderByDirection,
  selectedEvents,
  enableSelect,
  onSort,
  onChangePage,
  onSelect
}) => (
  <Fragment>
    <ResultsWrapper>
      <TableOverlay results={results} />
      <TableContainer className="panel">
        <TableWrapper>
          <thead>
            <TableRow>
              <TableHeader width={45} accessor={null} />
              <TableHeader
                width={416}
                accessor="name"
                sortedColumn={orderByColumn}
                sortDirection={orderByDirection}
                onClick={onSort}
              >
                Event Name
              </TableHeader>
              <TableHeader
                width={225}
                accessor="events"
                sortedColumn={orderByColumn}
                sortDirection={orderByDirection}
                onClick={onSort}
              >
                <CoolTip tip="The number of total event occurrences.">
                  <CFontIcon
                    className="fas fa-info-circle"
                    marginRight="0.5em"
                  />{" "}
                  Total Events
                </CoolTip>
              </TableHeader>
              <TableHeader
                width={225}
                accessor="users"
                sortedColumn={orderByColumn}
                sortDirection={orderByDirection}
                onClick={onSort}
              >
                <CoolTip tip="The number of unique users who triggered the event.">
                  <CFontIcon
                    className="fas fa-info-circle"
                    marginRight="0.5em"
                  />
                  Unique Users
                </CoolTip>
              </TableHeader>
              <TableHeader
                width={225}
                accessor="most_recent_timestamp"
                sortedColumn={orderByColumn}
                sortDirection={orderByDirection}
                onClick={onSort}
              >
                Last Triggered
              </TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {(results || []).map(
              ({ name, events, users, most_recent_timestamp }) => {
                const checked = (selectedEvents || []).some(
                  ({ source, event }) =>
                    source === "application" && event === name
                );
                return (
                  <TableRow key={name}>
                    <TableData>
                      <CoolTip
                        tip="You have selected the maximum of 4 events.  Deselect an event to add another."
                        disabled={enableSelect}
                      >
                        <CheckboxWrapper>
                          <CCheckbox
                            checked={checked}
                            disabled={!checked && !enableSelect}
                            onClick={() =>
                              onSelect({ source: "application", event: name })
                            }
                          />
                        </CheckboxWrapper>
                      </CoolTip>
                    </TableData>
                    <TableData>{name}</TableData>
                    <TableData>{events.toLocaleString()}</TableData>
                    <TableData>{users.toLocaleString()}</TableData>
                    <TableData>
                      {moment(most_recent_timestamp).format("lll")}
                    </TableData>
                  </TableRow>
                );
              }
            )}
          </tbody>
        </TableWrapper>
      </TableContainer>
    </ResultsWrapper>
    {totalResults > pageSize && (
      <Footer>
        <Pagination
          pageCount={Math.ceil(totalResults / pageSize)}
          currentIndex={currentPage}
          onChange={onChangePage}
        />
        <Text
          fontWeight="bold"
          position="absolute"
          top="calc(50% + 0.25em)"
          right={0}
        >
          Displaying {currentPage * pageSize + 1}-
          {Math.min(currentPage * pageSize + pageSize, totalResults)} of{" "}
          {totalResults}
        </Text>
      </Footer>
    )}
  </Fragment>
);

Application.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      events: PropTypes.number,
      users: PropTypes.number,
      most_recent_timestamp: PropTypes.number
    })
  ),
  currentPage: PropTypes.number,
  totalResults: PropTypes.number,
  pageSize: PropTypes.number,
  selectedEvents: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.string,
      event: PropTypes.string
    })
  ),
  enableSelect: PropTypes.bool,
  orderByColumn: PropTypes.string,
  orderByDirection: PropTypes.string,
  onSort: PropTypes.func,
  onChangePage: PropTypes.func,
  onSelect: PropTypes.func
};

const ApplicationWithAnalytics = ({
  startTime,
  searchTerm,
  pageSize = 10,
  selectedEvents,
  enableSelect,
  onSelect
}) => {
  const [orderByColumn, setOrderByColumn] = useState("events");
  const [orderByDirection, setOrderByDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => setCurrentPage(0), [
    startTime,
    searchTerm,
    pageSize,
    orderByColumn,
    orderByDirection
  ]);

  const handleSort = column => {
    if (column === orderByColumn) {
      setOrderByDirection(orderByDirection === "asc" ? "desc" : "asc");
    }
    setOrderByColumn(column);
  };

  const conditions = [
    ["event", "not like", "appcues:%"],
    ["event", "not like", "appcues_custom:%"],
    ...(searchTerm ? [["event", "ilike", `%${searchTerm}%`]] : [])
  ];

  return (
    <QueryResults
      query={{ conditions, metrics: ["names"], start_time: startTime }}
    >
      {([{ names: totalEvents }] = [{ names: 0 }]) => (
        <QueryResults
          query={{
            conditions,
            dimensions: ["name"],
            metrics: ["events", "users", "most_recent_timestamp"],
            order_by: [[orderByColumn, orderByDirection]],
            limit: pageSize,
            offset: currentPage * pageSize,
            start_time: startTime
          }}
        >
          {results => (
            <Application
              results={results}
              currentPage={currentPage}
              totalResults={totalEvents}
              pageSize={pageSize}
              orderByColumn={orderByColumn}
              orderByDirection={orderByDirection}
              onSort={handleSort}
              onChangePage={setCurrentPage}
              selectedEvents={selectedEvents}
              enableSelect={enableSelect}
              onSelect={onSelect}
            />
          )}
        </QueryResults>
      )}
    </QueryResults>
  );
};

ApplicationWithAnalytics.propTypes = {
  startTime: PropTypes.string,
  segmentId: PropTypes.string,
  searchTerm: PropTypes.string,
  pageSize: PropTypes.number,
  selectedEvents: PropTypes.arrayOf(
    PropTypes.shape({
      source: PropTypes.string,
      event: PropTypes.string
    })
  ),
  enableSelect: PropTypes.bool,
  onSelect: PropTypes.func
};

export default ApplicationWithAnalytics;
